import { Navigate, Outlet } from 'react-router-dom';
import { useCompetitionContext, useCompetitionIdParam } from '@/hooks/useCompetitions';
import { PageSpinner } from '@/components/ui/Spinner';

export type CompetitionRoleRequirement = 'manage' | 'judge' | 'participant';

/**
 * Competition-scoped route guard - reads :competitionId straight from the
 * URL (not from the switcher's selection) and checks the caller's actual
 * relationship to THAT competition via GET /competitions/{id}/my-context.
 * This closes a gap the old global-role RoleRoute had: an organizer could
 * previously open /organizer/competitions/{anyId}/... for a competition they
 * don't manage, since the guard only checked their platform-wide role, never
 * the specific id in the URL.
 */
export function CompetitionRoleRoute({ allow }: { allow: CompetitionRoleRequirement[] }) {
  const ref = useCompetitionIdParam();
  const competitionId = ref.competitionId || undefined;
  const { data, isLoading } = useCompetitionContext(competitionId);

  if (ref.isError) return <Navigate to="/" replace />;
  if (ref.isLoading || isLoading) return <PageSpinner />;
  if (!competitionId) return <Navigate to="/" replace />;

  const permitted =
    (allow.includes('manage') && data?.canManage) ||
    (allow.includes('judge') && data?.isJudge) ||
    (allow.includes('participant') && data?.isParticipant);

  if (!permitted) return <Navigate to="/" replace />;

  return <Outlet />;
}
